type PhoneInfo = {
  ddi: string
  nacional: string
  pais: string
}

const MASKS: Record<string, string> = {
  BR: '(##) #####-####',
  US: '(###) ###-####',
  PT: '### ### ###',
  AR: '### ###-####',
  MX: '(###) ###-####',
  CO: '(###) ###-####',
  ES: '### ### ###',
  GB: '##### #####',
  DE: '#### ######',
  FR: '# ## ## ## ##',
  IT: '### ### ####',
  CL: '# #### ####',
}

function applyMask(digits: string, mask: string): string {
  let i = 0
  return mask
    .split('')
    .map((char) => (char === '#' ? digits[i++] ?? '' : char))
    .join('')
    .replace(/[\s\-(]+$/, '')
}

export function normalizarPhone(phone: string): PhoneInfo {
  const digits = phone.replace(/\D/g, '')

  if (digits.length === 12 && digits.startsWith('351'))
    return { ddi: '+351', nacional: digits.slice(3), pais: 'PT' }

  if (digits.length === 13 && digits.startsWith('55'))
    return { ddi: '+55', nacional: digits.slice(2), pais: 'BR' }

  if (digits.length === 12 && digits.startsWith('54'))
    return { ddi: '+54', nacional: digits.slice(2), pais: 'AR' }

  if (digits.length === 12 && digits.startsWith('52'))
    return { ddi: '+52', nacional: digits.slice(2), pais: 'MX' }

  if (digits.length === 12 && digits.startsWith('57'))
    return { ddi: '+57', nacional: digits.slice(2), pais: 'CO' }

  if (digits.length === 11 && digits.startsWith('34'))
    return { ddi: '+34', nacional: digits.slice(2), pais: 'ES' }

  if (digits.length === 12 && digits.startsWith('44'))
    return { ddi: '+44', nacional: digits.slice(2), pais: 'GB' }

  if (digits.length === 12 && digits.startsWith('49'))
    return { ddi: '+49', nacional: digits.slice(2), pais: 'DE' }

  if (digits.length === 11 && digits.startsWith('33'))
    return { ddi: '+33', nacional: digits.slice(2), pais: 'FR' }

  if (digits.length === 12 && digits.startsWith('39'))
    return { ddi: '+39', nacional: digits.slice(2), pais: 'IT' }

  if (digits.length === 11 && digits.startsWith('56'))
    return { ddi: '+56', nacional: digits.slice(2), pais: 'CL' }

  if (digits.length === 11 && digits.startsWith('1'))
    return { ddi: '+1', nacional: digits.slice(1), pais: 'US' }

  return { ddi: '+55', nacional: digits, pais: 'BR' }
}

export function hasValidPhone(phone: string | null | undefined): boolean {
  if (!phone) return false
  return phone.replace(/\D/g, '').length >= 8
}

/**
 * Formata para exibição. Com ddi/iso explícitos (vindos da API desde a Fase
 * 2.5), formata direto sem adivinhar — necessário porque nesse caso "phone"
 * já é o nacional puro, sem DDI concatenado, e normalizarPhone não teria
 * como decidir o país sozinho. Sem ddi/iso (chamador legado, ou registro
 * ainda não migrado), cai na heurística antiga — toda a base histórica é
 * BR, e o comportamento fica idêntico ao de antes desta mudança.
 */
export function formatarPhone(
  phone: string | null | undefined,
  ddi?: string | null,
  iso?: string | null
): string {
  if (!phone) return '—'
  const digits = phone.replace(/\D/g, '')
  if (!digits) return '—'
  if (digits.length < 8) return digits

  if (ddi && iso) {
    const mask = MASKS[iso]
    if (!mask) return `+${ddi} ${digits}`
    const expectedLen = mask.replace(/[^#]/g, '').length
    if (digits.length !== expectedLen) return `+${ddi} ${digits}`
    return `+${ddi} ${applyMask(digits, mask)}`
  }

  const { ddi: ddiLegado, nacional, pais } = normalizarPhone(digits)
  const mask = MASKS[pais]
  const expectedLen = mask.replace(/[^#]/g, '').length

  if (nacional.length < expectedLen) return digits

  return `${ddiLegado} ${applyMask(nacional, mask)}`
}

/** Link de WhatsApp. Mesma regra de ddi/iso explícitos de formatarPhone. */
export function linkWhatsApp(
  phone: string | null | undefined,
  ddi?: string | null,
  iso?: string | null
): string | null {
  if (!hasValidPhone(phone)) return null
  const digits = phone!.replace(/\D/g, '')

  if (ddi && iso) return `https://wa.me/${ddi}${digits}`

  const { ddi: ddiLegado, nacional } = normalizarPhone(digits)
  return `https://wa.me/${ddiLegado.replace('+', '')}${nacional}`
}
